import React from "react"
import {BookOpen, Wrench, AlertCircle, Download} from 'lucide-react'

const GuidesPage = () => {
  const guides = [
    {
      title: "How to Add a Generator Auto Start to a Manual Generator",
      category: "Installation",
      description: "Complete step-by-step guide for retrofitting automatic start capability to generators with manual or electric start systems.",
      difficulty: "Intermediate",
      time: "2-3 hours"
    },
    {
      title: "The Best Remote Start Portable Generator for RV Camping",
      category: "Buying Guide",
      description: "Compare top portable generators with remote start capability, including Honda, Predator, and Westinghouse models for RV applications.",
      difficulty: "Beginner",
      time: "15 min read"
    },
    {
      title: "Troubleshooting an Autostart Generator That Fails to Crank",
      category: "Troubleshooting",
      description: "Diagnose and fix common issues preventing automatic generator starting, including battery, relay, and control module problems.",
      difficulty: "Intermediate",
      time: "30 min read"
    },
    {
      title: "Integrating 2-Wire Start with Victron Solar Systems",
      category: "Solar Integration",
      description: "Configure Victron MultiPlus or Quattro inverters to automatically start your backup generator when battery voltage drops.",
      difficulty: "Advanced",
      time: "1-2 hours"
    },
    {
      title: "Remote Start Kit Wiring Diagrams by Brand",
      category: "Technical",
      description: "Detailed wiring diagrams for Honda, Onan, Generac, and other popular generator brands with component specifications.",
      difficulty: "Intermediate",
      time: "Reference"
    },
    {
      title: "Setting Up Generator Auto Start with Growatt Inverters",
      category: "Solar Integration",
      description: "Configure Growatt SPF series inverters for automatic generator control based on battery state of charge.",
      difficulty: "Advanced",
      time: "1-2 hours"
    }
  ]

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-500 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center space-x-3 mb-4">
              <BookOpen size={40} />
              <h1 className="text-4xl md:text-5xl font-bold">
                Installation Guides & Resources
              </h1>
            </div>
            <p className="text-xl text-orange-50 leading-relaxed">
              Comprehensive guides for installing, configuring, and troubleshooting generator auto start and remote start systems.
            </p>
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guides.map((guide, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl transition-shadow duration-200">
                <div className="flex items-start justify-between mb-4">
                  <span className="inline-block px-3 py-1 bg-orange-100 text-orange-600 text-sm font-semibold rounded-full">
                    {guide.category}
                  </span>
                  {guide.category === "Installation" && <Wrench className="text-gray-400" size={20} />}
                  {guide.category === "Troubleshooting" && <AlertCircle className="text-gray-400" size={20} />}
                  {guide.category === "Technical" && <Download className="text-gray-400" size={20} />}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {guide.title}
                </h3>
                
                <p className="text-gray-600 mb-4">
                  {guide.description}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        guide.difficulty === "Beginner" ? "bg-green-500" :
                        guide.difficulty === "Intermediate" ? "bg-orange-500" :
                        "bg-red-500"
                      }`}></span>
                      {guide.difficulty}
                    </span>
                    <span>{guide.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Tips Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Quick Installation Tips
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Before You Start</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Verify your generator has electric start capability (12V starter system)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Read the generator manual to understand the electrical system</span>
                </li>
                <li className="flex items-start">
                  <span className="text-orange-500 mr-2">✓</span>
                  <span>Gather necessary tools: multimeter, wire strippers, crimpers, zip ties</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Safety First</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">⚠</span>
                  <span>Always disconnect the spark plug before working on electrical systems</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">⚠</span>
                  <span>Use proper gauge wire rated for starter current (typically 10-12 AWG)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">⚠</span>
                  <span>Install inline fuses on all power connections to protect equipment</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Can I add auto start to a pull-start only generator?
              </h3>
              <p className="text-gray-700">
                No, automatic start systems require an electric starter motor. You would need to upgrade to an electric start kit first, which is available for some generator models but may not be cost-effective.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                What's the difference between remote start and auto start?
              </h3>
              <p className="text-gray-700">
                Remote start requires manual activation via a key fob or app. Auto start systems activate automatically based on conditions like low battery voltage or power outage, without any manual intervention.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                How much does a typical installation cost?
              </h3>
              <p className="text-gray-700">
                DIY remote start kits range from $150-$400 depending on features. Professional installation adds $200-$500. Auto start modules for solar integration typically cost $100-$300 plus installation time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need More Help?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Can't find the guide you're looking for? Check back regularly as we add new content and tutorials.
          </p>
        </div>
      </section>
    </div>
  )
}

export default GuidesPage
